import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Label,
  Input,
  FormGroup,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import FileFacesItem from './FacesItem';
import FileSidebarEmptyState from './EmptyState';

import { sendTrackingEvent } from '../../../helpers';
import {
  fileSupportsFaceRecognition,
  isFileProcessing,
} from '../../../helpers/Files';

const FileFaces = ({ file, readOnly }) => {
  const [showHidden, setShowHidden] = useState(false);

  const intl = useIntl();

  const changeShowHidden = (e) => {
    const { checked } = e.target;
    setShowHidden(checked);

    if (checked) {
      sendTrackingEvent('file-faces-sidebar-show-hidden');
    }
  };

  const faces = file.faces ? file.faces.filter(f => (!f.hidden || showHidden)) : [];
  const hiddenFaces = useMemo(() => (
    file.faces ? file.faces.filter(f => (f.hidden)) : []
  ), [file.faces]);

  useEffect(() => {
    sendTrackingEvent('file-faces-sidebar-opened');
  }, []);

  useEffect(() => {
    if (hiddenFaces.length === 0) {
      setShowHidden(false);
    }
  }, [hiddenFaces]);

  if (!file || !file.faces || !fileSupportsFaceRecognition(file)) {
    return null;
  }

  return (
    <div className="file-faces file-details-item file-sidebar-content-scrollable">
      <div className="file-detail-header file-detail-header-light">
        <span>
          {intl.formatMessage({ defaultMessage: 'People in this image' })}
        </span>
        {(hiddenFaces.length > 0) && (
          <FormGroup check className="file-faces-show-switch">
            <Label htmlFor="file-faces-show-hidden" check>
              <Input
                type="checkbox"
                id="file-faces-show-hidden"
                name="show-hidden"
                value={true}
                checked={showHidden}
                onChange={changeShowHidden}
              />
              {intl.formatMessage({ defaultMessage: 'show hidden faces' })}
            </Label>
          </FormGroup>
        )}
      </div>
      <div className="file-detail-body file-detail-body-light">
        <div className="file-faces-body">
          {faces.map(face => (
            <FileFacesItem
              key={face.uuid}
              face={face}
              file={file}
              readOnly={readOnly}
            />
          ))}
        </div>
        {faces.length === 0 && (
          <>
            {isFileProcessing(file, 'faces') ? (
              <FileSidebarEmptyState
                icon="fa-regular fa-sparkles fa-fade"
                info={intl.formatMessage({ defaultMessage: 'Looking for people in your image...' })}
                title={intl.formatMessage({ defaultMessage: 'Facial recognition' })}
              />
            ) : (
              <FileSidebarEmptyState
                info={intl.formatMessage({ defaultMessage: 'No faces detected on this image' })}
                title={intl.formatMessage({ defaultMessage: 'Facial recognition' })}
              />
            )}
          </>
        )}
        {!fileSupportsFaceRecognition(file) && (
          <FileSidebarEmptyState
            info={intl.formatMessage({ defaultMessage: 'File type not supported by Facial Recognition' })}
            title={intl.formatMessage({ defaultMessage: 'Facial recognition' })}
          />
        )}
      </div>
    </div>
  );
};

FileFaces.propTypes = {
  file: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

FileFaces.defaultProps = {
  readOnly: false,
};

export default FileFaces;
